import React from 'react';
import { Alert, Box, Typography } from '@mui/material';
import PasoAPaso from './utils/PasoAPaso.jsx';
import imgExcelHoja from '../images/ayuda/precios/01-excel-hoja.png';
import imgExcelColumnas from '../images/ayuda/precios/02-excel-columnas.png';
import imgInicio from '../images/ayuda/precios/03-inicio.jpg';
import imgSubir from '../images/ayuda/precios/04-subir.jpg';
import imgHojaResumen from '../images/ayuda/precios/05-hoja-resumen.jpg';
import imgSinReconocer from '../images/ayuda/precios/06-sin-reconocer.jpg';
import imgPlanilla from '../images/ayuda/precios/07-planilla.jpg';
import imgElegir from '../images/ayuda/precios/08-elegir-productos.jpg';
import imgCambios from '../images/ayuda/precios/09-cambios.jpg';
import imgConfirmar from '../images/ayuda/precios/10-confirmar.jpg';
import imgResultado from '../images/ayuda/precios/11-resultado.jpg';

// Guía paso a paso para actualizar los precios con el Excel de Grido. Es
// contenido (textos + capturas), no lógica: las capturas están en
// src/images/ayuda/precios/ y los números rojos de cada una se explican en el
// texto del paso. Si cambia alguna pantalla, volver a sacar la captura.

const Lista = ({ children }) => (
  <Box component="ul" sx={{ m: 0, mt: 1, pl: 3, '& li': { mb: 0.5 } }}>{children}</Box>
);

const PASOS = [
  {
    corto: 'Excel',
    titulo: 'Abrí el Excel de Grido y fijate que tenga la pestaña correcta',
    texto: (
      <>
        Es el archivo <strong>"Listas de Precios CENTRO-LITORAL"</strong> que te manda Grido (tiene que ser{' '}
        <strong>.xlsx</strong>). Abajo tiene varias pestañas: la que hay que usar es la que dice{' '}
        <strong>Grido</strong> <strong>(1)</strong>. La pestaña "Precio Helacor" tiene otros valores y <strong>no</strong> se usa.
      </>
    ),
    nota: {
      tipo: 'warning',
      texto: 'Si más adelante ves aumentos enormes o raros, casi seguro se eligió la pestaña equivocada.',
    },
    imagenes: [{
      src: imgExcelHoja, w: 618, h: 150, ancho: 540, anchoZoom: 760,
      alt: 'Pestañas del Excel: la correcta es "Precios Grido"',
      leyenda: 'La pestaña correcta es la que dice Grido.',
    }],
  },
  {
    corto: 'Columnas',
    titulo: 'Cómo lee el sistema la lista',
    texto: (
      <>
        El sistema lee <strong>dos columnas</strong>: el <strong>nombre del producto</strong> <strong>(1)</strong> y su{' '}
        <strong>precio</strong>, la columna que dice "PAIS" <strong>(2)</strong>. Los títulos de sección ("HELADO POR BOCHA / KG.",
        "POTES"…) y las filas vacías se saltean solos. Por eso: <strong>no muevas, borres ni agregues columnas</strong>, no
        cambies los nombres de los productos y dejá los precios como números (no como texto).
      </>
    ),
    imagenes: [{
      src: imgExcelColumnas, w: 618, h: 760, ancho: 560, anchoZoom: 800,
      alt: 'Columna de nombres (1) y columna de precios PAIS (2) del Excel',
      leyenda: '(1) Nombre del producto · (2) Precio.',
    }],
  },
  {
    corto: 'Entrar',
    titulo: 'Entrá a "Actualizar precios"',
    texto: (
      <>
        Desde el inicio tocá la tarjeta <strong>"Actualizar precios"</strong> <strong>(1)</strong>. También la encontrás en el menú de
        arriba. Solo la ven los usuarios <strong>Administrador</strong> y <strong>Franquiciado</strong>.
      </>
    ),
    imagenes: [{ src: imgInicio, w: 1970, h: 628, alt: 'Pantalla de inicio con la tarjeta "Actualizar precios" marcada' }],
  },
  {
    corto: 'Subir',
    titulo: 'Tocá "Subir Excel" y elegí el archivo',
    texto: (
      <>
        Tocá <strong>"Subir Excel"</strong> <strong>(1)</strong>: se abre el explorador de archivos de tu computadora. Elegí el Excel de
        Grido (<strong>.xlsx</strong>, hasta 15 MB) y esperá unos segundos mientras se sube y se lee.
      </>
    ),
    nota: {
      tipo: 'info',
      texto: 'El archivo queda guardado de forma temporal (unas 2 horas) solo para que puedas revisar los cambios y aplicarlos. Si pasa mucho tiempo y dice que "el archivo ya no está disponible", volvé a subirlo.',
    },
    imagenes: [{ src: imgSubir, w: 2000, h: 422, alt: 'Pantalla "Actualizar precios" con el botón "Subir Excel" marcado' }],
  },
  {
    corto: 'Hoja y resumen',
    titulo: 'Controlá la hoja y mirá el resumen',
    texto: (
      <>
        El sistema elige solo la hoja <strong>"Precios Grido"</strong> <strong>(1)</strong> ("hoja" es cada pestaña de abajo del Excel, las del
        paso 1). <strong>Controlá que diga exactamente eso.</strong> Si dice otra cosa (por ejemplo "Precio Helacor"), tocá ese cuadro, elegí{' '}
        <strong>"Precios Grido"</strong> y los números de abajo se recalculan solos. Debajo aparece un{' '}
        <strong>resumen</strong> <strong>(2)</strong> con cuatro números:
        <Lista>
          <li><strong>Con cambio de precio</strong>: los productos que van a cambiar de precio.</li>
          <li><strong>Sin cambios</strong>: los que ya tienen ese precio.</li>
          <li><strong>Sin mapear</strong> (naranja): líneas del Excel que el sistema todavía no reconoce.</li>
          <li><strong>Mapeadas sin producto</strong> (rojo): la línea se reconoce, pero el producto ya no existe o está inactivo.</li>
        </Lista>
      </>
    ),
    imagenes: [{ src: imgHojaResumen, w: 1970, h: 471, alt: 'Hoja elegida (1) y resumen de cuatro números (2)' }],
  },
  {
    corto: 'Sin reconocer',
    titulo: 'Si hay líneas sin reconocer o sin producto: indicá a qué producto corresponden',
    texto: (
      <>
        Puede aparecer un cartel naranja <strong>(1)</strong> con los nombres del Excel que no se reconocen. <strong>No es un error</strong>:
        pasa cuando Grido agrega un producto nuevo o le cambia el nombre a uno. Tocá <strong>"Agregar al mapeo"</strong> <strong>(2)</strong> para
        decirle al sistema a qué producto corresponde cada una y seguí en el paso 7.
        <Box sx={{ mt: 1 }}>
          Si aparece un cartel <strong>rojo</strong> (líneas "mapeadas pero el producto no existe o está inactivo"), ese cartel no tiene botón:
          tocá <strong>"Ver mapeo de productos"</strong> (en la parte de arriba de la pantalla) y resolvelo igual, en el paso 7.
        </Box>
        <Box sx={{ mt: 1 }}>
          Si no aparece ningún cartel naranja ni rojo, seguí en el paso 9.
        </Box>
      </>
    ),
    nota: {
      tipo: 'info',
      texto: 'Mientras una línea esté sin mapear o sin producto, ese producto no se actualiza (el resto sí).',
    },
    imagenes: [{ src: imgSinReconocer, w: 1970, h: 576, alt: 'Cartel naranja con las líneas sin reconocer (1) y botón "Agregar al mapeo" (2)' }],
  },
  {
    corto: 'Planilla',
    titulo: 'Mirá tu Excel línea por línea',
    texto: (
      <>
        En "Mapeo de precios" se abre la pestaña <strong>"Planilla del Excel"</strong>: tu lista, en el mismo orden. En{' '}
        <strong>verde</strong> <strong>(1)</strong> están las líneas que ya se reconocen ("Mapeado" y el producto del sistema). En{' '}
        <strong>rosado</strong> <strong>(2)</strong> las que faltan: pueden decir "Sin mapear" (como SMOOTHIE en la imagen) o "Sin producto". En
        cada una tocá <strong>"Elegir producto(s)"</strong> <strong>(3)</strong>.
      </>
    ),
    imagenes: [{ src: imgPlanilla, w: 2000, h: 746, alt: 'Planilla del Excel con filas verdes (1), filas rosadas (2) y el botón "Elegir producto(s)" (3)' }],
  },
  {
    corto: 'Elegir producto',
    titulo: 'Elegí a qué producto corresponde',
    texto: (
      <>
        Escribí el nombre en el buscador <strong>(1)</strong> y tildá el producto <strong>(2)</strong>. Una misma línea del Excel puede
        agrupar varios productos: por ejemplo <strong>SMOOTHIE</strong> es una sola línea y corresponde a todos los sabores; tildalos
        todos y, cuando cambie el precio de esa línea, se actualizan juntos. Si el producto todavía no existe en el sistema, tocá{' '}
        <strong>"¿No está en el catálogo? Crear producto nuevo"</strong> <strong>(3)</strong>: se abre un formulario que pide la descripción, el
        grupo y el <strong>ID Club Grido</strong> del producto (si no lo sabés, pedíselo al administrador). Tocá <strong>"Crear y sumar"</strong>
        y el producto queda tildado en la lista. Para terminar, tocá <strong>"Mapear"</strong> <strong>(4)</strong>.
        <Box sx={{ mt: 1 }}>
          Cuando termines, volvé con <strong>"Volver a Actualizar precios"</strong>: tu Excel sigue cargado (mientras no hayas aplicado los
          cambios), no hace falta subirlo de nuevo.
        </Box>
      </>
    ),
    nota: {
      tipo: 'info',
      texto: 'Lo que mapeás y los productos que creás se guardan al instante (no esperan al paso 10). Se hace una sola vez por nombre: la próxima vez que subas un Excel, esa línea ya se reconoce sola.',
    },
    imagenes: [{ src: imgElegir, w: 1264, h: 1247, ancho: 640, anchoZoom: 1000, alt: 'Selector de productos: buscador (1), productos tildados (2), crear producto nuevo (3) y botón Mapear (4)' }],
  },
  {
    corto: 'Revisar cambios',
    titulo: 'Revisá los cambios de precio',
    texto: (
      <>
        En <strong>"Cambios de precio"</strong> <strong>(1)</strong> están todos los productos que van a cambiar, agrupados por categoría:
        precio actual, precio nuevo y la diferencia (verde si sube, rojo si baja). Mirá que tenga sentido. Cuando esté todo bien, tocá{' '}
        <strong>"Aplicar … cambios"</strong> <strong>(2)</strong>; el número es la cantidad de productos que se van a actualizar.
      </>
    ),
    nota: {
      tipo: 'warning',
      texto: 'Si ves cambios muy grandes o raros, no apliques: revisá que el archivo subido y la hoja elegida sean los correctos (pasos 1 y 5).',
    },
    imagenes: [{ src: imgCambios, w: 1970, h: 737, alt: 'Tabla de cambios de precio (1) y botón "Aplicar cambios" (2)' }],
  },
  {
    corto: 'Confirmar',
    titulo: 'Confirmá',
    texto: (
      <>
        Aparece un cartel de confirmación. Tocá <strong>"Confirmar"</strong> <strong>(1)</strong> para actualizar los precios. Ojo: desde esta
        pantalla <strong>no se puede deshacer</strong>.
      </>
    ),
    imagenes: [{ src: imgConfirmar, w: 1264, h: 417, ancho: 640, anchoZoom: 900, alt: 'Cartel "Aplicar cambios de precio" con el botón Confirmar (1)' }],
  },
  {
    corto: 'Listo',
    titulo: '¡Listo! Precios actualizados',
    texto: (
      <>
        Vas a ver un tilde verde y un mensaje <strong>(1)</strong> con cuántos precios se actualizaron. Las líneas "sin mapear" o "sin producto"
        (los números entre paréntesis) no se tocaron: esos productos siguen con el precio viejo. Para actualizarlos, mapealas (pasos 6 a 8) y
        volvé a subir el mismo Excel (paso 4): los que ya cambiaron van a figurar en "Sin cambios" y solo se modifican los que faltaban.
      </>
    ),
    nota: {
      tipo: 'info',
      texto: 'En las otras computadoras que ya tengan el sistema abierto, los precios nuevos se ven al cerrar y volver a abrir el sistema, o tocando "Actualizar catálogo" en Registrar Egreso.',
    },
    imagenes: [{ src: imgResultado, w: 2000, h: 557, alt: 'Mensaje de éxito con la cantidad de precios actualizados (1)' }],
  },
];

const EXTRA = (
  <Box sx={{ mt: 1 }}>
    <Typography variant="h6" component="h3" sx={{ fontWeight: 700, mb: 1 }}>Cosas para tener en cuenta</Typography>
    <Lista>
      <li>Los precios son <strong>del sistema completo</strong>: al aplicar, cambian en todas las sucursales.</li>
      <li>
        No hay botón de deshacer, pero queda registrado quién cambió cada precio y cuándo (en "Auditoría de cambios", para el
        Administrador). Para volver atrás, subí el Excel de la lista anterior.
      </li>
      <li>Si el Excel tiene una línea con un nombre que el sistema no conoce, ese producto simplemente no se actualiza.</li>
      <li>
        Si el administrador configuró el precio de la licencia mensual en base a un producto (por ejemplo, el kilo de helado), el monto
        de la licencia también se ajusta cuando cambia el precio de ese producto.
      </li>
    </Lista>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>Si algo sale mal</Typography>
    <Lista>
      <li><strong>"Solo se acepta formato .xlsx"</strong>: guardá el archivo como Excel (.xlsx) y subilo de nuevo.</li>
      <li><strong>Aparecen aumentos enormes</strong>: casi seguro quedó seleccionada otra hoja en el desplegable "Hoja"; elegí "Precios Grido".</li>
      <li><strong>No cambia nada o todo figura "sin mapear"</strong>: revisá que sea el archivo correcto y que no se hayan movido las columnas.</li>
      <li><strong>"El archivo subido ya no está disponible"</strong>: pasó demasiado tiempo, volvé a subirlo.</li>
    </Lista>
    <Alert severity="info" sx={{ mt: 2 }}>
      ¿Seguís con dudas? Consultá con el administrador del sistema antes de aplicar los cambios.
    </Alert>
  </Box>
);

function GuiaActualizarPrecios({ open, onClose }) {
  return (
    <PasoAPaso
      open={open}
      onClose={onClose}
      titulo="Actualizar precios, paso a paso"
      intro={(
        <Typography variant="body1">
          Cuando Grido te manda la lista de precios nueva (en general una vez por mes) podés actualizar todos los precios del sistema
          de una sola vez subiendo ese Excel. <strong>Ningún precio cambia hasta que vos lo confirmás</strong> (lo que mapeás o los
          productos nuevos que creás en el paso 8 sí se guardan apenas lo hacés), así que podés revisar tranquilo. Los números rojos de
          las imágenes se explican en cada paso.
        </Typography>
      )}
      pasos={PASOS}
      extra={EXTRA}
    />
  );
}

export default GuiaActualizarPrecios;
